import apiService, { ApiError } from './apiService';

/**
 * Admin User Service
 * Handles all admin user management API interactions.
 */
class AdminUserService {
  /**
   * Fetch paginated list of admin users
   * @param {Object} params
   * @param {number} [params.page=1]
   * @param {number} [params.size=20]
   * @param {string} [params.keyword]
   * @param {string} [params.role]
   * @param {boolean} [params.active]
   * @param {string} [params.order]
   * @returns {Promise<Object>} API response with paginated admin users
   */
  async getUsers(params = {}) {
    try {
      const response = await apiService.skaiOneRequest('/api/v1/admin/users/list', {
        method: 'POST',
        data: {
          page: params.page || 1,
          size: params.size || 20,
          keyword: params.keyword || '',
          role: params.role || '',
          active: typeof params.active === 'boolean' ? params.active : undefined,
          order: params.order || 'au.created_at DESC',
        },
      });

      return response;
    } catch (error) {
      console.error('Failed to get admin users:', error);
      throw error;
    }
  }

  /**
   * Search skaix_account by email, phone, or name
   * @param {string} keyword
   * @returns {Promise<Object>} List of matching accounts
   */
  async searchAccounts(keyword) {
    try {
      const response = await apiService.skaiOneRequest(
        `/api/v1/admin/users/search?keyword=${encodeURIComponent(keyword || '')}`,
        {
          method: 'GET',
        }
      );
      return response;
    } catch (error) {
      console.error('Failed to search accounts:', error);
      throw error;
    }
  }

  /**
   * Add an existing skaix_account as admin
   * @param {Object} data
   * @param {string} data.accountId
   * @param {string} data.role
   * @returns {Promise<Object>} Added admin user details
   */
  async addExistingUser(data) {
    try {
      const response = await apiService.skaiOneRequest('/api/v1/admin/users/existing', {
        method: 'POST',
        data: {
          accountId: data.accountId,
          role: data.role,
        },
      });
      return response;
    } catch (error) {
      console.error('Failed to add existing user as admin:', error);
      throw error;
    }
  }

  /**
   * Invite and create new account with auto-generated temporary password
   * @param {Object} data
   * @param {string} data.name
   * @param {string} data.email
   * @param {string} [data.phone]
   * @param {string} data.role
   * @returns {Promise<Object>} Created admin user details
   */
  async inviteUser(data) {
    try {
      const response = await apiService.skaiOneRequest('/api/v1/admin/users/invite', {
        method: 'POST',
        data: {
          name: data.name,
          email: data.email,
          phone: data.phone || '',
          role: data.role,
        },
      });
      return response;
    } catch (error) {
      console.error('Failed to invite admin user:', error);
      throw error;
    }
  }

  /**
   * Update admin user role, status, or contact details (synced to skaix_account)
   * @param {string} id
   * @param {Object} data
   * @param {string} [data.name]
   * @param {string} [data.email]
   * @param {string} [data.phone]
   * @param {string} [data.role]
   * @param {boolean} [data.active]
   * @returns {Promise<Object>} Updated admin user details
   */
  async updateUser(id, data) {
    try {
      const response = await apiService.skaiOneRequest(`/api/v1/admin/users/${id}`, {
        method: 'PUT',
        data,
      });
      return response;
    } catch (error) {
      console.error('Failed to update admin user:', error);
      throw error;
    }
  }

  /**
   * Remove admin privileges (delete from admin_users)
   * @param {string} id
   * @returns {Promise<Object>}
   */
  async deleteUser(id) {
    try {
      const response = await apiService.skaiOneRequest(`/api/v1/admin/users/${id}`, {
        method: 'DELETE',
      });
      return response;
    } catch (error) {
      console.error('Failed to delete admin user:', error);
      throw error;
    }
  }
}

export const adminUserService = new AdminUserService();
export { AdminUserService };
export default adminUserService;
